/**
 * Typed calls for `/subscriptions/admin/*` and `/subscriptions/admin/webhook-urls`
 * — Beheer › Abonnementen (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2). These
 * operations' 200/201 bodies are fully declared in `src/api/schema.ts`, so
 * `apiGet`/`apiPost`/`apiPut` infer their return types directly — no casts.
 *
 * Scope: admin-only. The caller-facing Mollie subscription lives in
 * `src/features/subscription/api.ts` — never touch that from here.
 */

import { apiGet, apiPost, apiPut } from '@/api/client';
import type { components } from '@/api/schema';
import type { TranslationKey } from '@/i18n/types';

export type SubscriptionProvider = components['parameters']['SubscriptionsProviderPath'];

export type SubscriptionProviderConfigField =
  components['schemas']['SubscriptionProviderConfigField'];

/** Map of config_key => field. The valid key set is fixed per provider and
 * not part of the API response — see `PROVIDER_CONFIG_FIELDS` below. */
export type SubscriptionProviderConfig = components['schemas']['SubscriptionProviderConfig'];

export type SubscriptionAdminProduct = components['schemas']['SubscriptionAdminProduct'];

export type SubscriptionAdminMollieSubscription =
  components['schemas']['SubscriptionMollieSubscription'];

export type SubscriptionAdminLedger = components['schemas']['SubscriptionAdminLedger'];

export type SubscriptionAdminPurchase = components['schemas']['SubscriptionAdminPurchase'];

/** `{ ok, message? }` — always a 200, the result is in `ok`, never the status. */
export type SubscriptionTestConnectionResult = Awaited<ReturnType<typeof testProviderConnection>>;

export type SubscriptionWebhookUrls = Awaited<ReturnType<typeof getWebhookUrls>>;

/**
 * The fixed field set per provider (SUBSCRIPTIONS-FRONTEND-BRIEFING.md § 2 /
 * `SubscriptionProviderConfig` in openapi.yaml — never part of the runtime
 * response, so the client hardcodes it here rather than showing a free
 * key-value form).
 */
export interface ProviderConfigFieldSpec {
  key: string;
  secret: boolean;
  labelKey: TranslationKey;
  /** Fixed value set (openapi.yaml's `SubscriptionProviderConfig` description) — renders as a
   * dropdown instead of a free-text input. */
  options?: readonly string[];
}

export const PROVIDER_CONFIG_FIELDS: Record<
  SubscriptionProvider,
  readonly ProviderConfigFieldSpec[]
> = {
  apple: [
    {
      key: 'issuer_id',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_issuer_id',
    },
    { key: 'key_id', secret: false, labelKey: 'manageSubscriptions.config.fieldLabel_key_id' },
    {
      key: 'p8_private_key',
      secret: true,
      labelKey: 'manageSubscriptions.config.fieldLabel_p8_private_key',
    },
    {
      key: 'bundle_id',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_bundle_id',
    },
    {
      key: 'environment',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_environment',
      options: ['sandbox', 'production'],
    },
  ],
  google: [
    {
      key: 'service_account_json',
      secret: true,
      labelKey: 'manageSubscriptions.config.fieldLabel_service_account_json',
    },
    {
      key: 'package_name',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_package_name',
    },
    {
      key: 'pubsub_audience',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_pubsub_audience',
    },
    {
      key: 'pubsub_service_account_email',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_pubsub_service_account_email',
    },
  ],
  mollie: [
    {
      key: 'api_key_test',
      secret: true,
      labelKey: 'manageSubscriptions.config.fieldLabel_api_key_test',
    },
    {
      key: 'api_key_live',
      secret: true,
      labelKey: 'manageSubscriptions.config.fieldLabel_api_key_live',
    },
    {
      key: 'active_environment',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_active_environment',
      options: ['test', 'live'],
    },
    {
      key: 'webhook_signing_secret',
      secret: true,
      labelKey: 'manageSubscriptions.config.fieldLabel_webhook_signing_secret',
    },
    {
      key: 'redirect_url',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_redirect_url',
    },
    {
      key: 'cancel_url',
      secret: false,
      labelKey: 'manageSubscriptions.config.fieldLabel_cancel_url',
    },
  ],
};

export const PROVIDER_LABEL_KEYS: Record<SubscriptionProvider, TranslationKey> = {
  apple: 'manageSubscriptions.config.providerApple',
  google: 'manageSubscriptions.config.providerGoogle',
  mollie: 'manageSubscriptions.config.providerMollie',
};

export type WebhookUrlKey =
  'mollie_classic' | 'mollie_next_gen' | 'apple_production' | 'apple_sandbox' | 'google_pubsub';

/** Which of the five fixed `webhook-urls` keys belong under each provider's
 * config, so the Config screen can show a provider's own webhook URLs
 * instead of a separate Webhooks menu item. */
export const PROVIDER_WEBHOOK_KEYS: Record<SubscriptionProvider, readonly WebhookUrlKey[]> = {
  apple: ['apple_production', 'apple_sandbox'],
  google: ['google_pubsub'],
  mollie: ['mollie_classic', 'mollie_next_gen'],
};

export function getProviderConfig(provider: SubscriptionProvider) {
  return apiGet('/subscriptions/admin/config/{provider}', {
    params: { provider },
  });
}

/** Every value is sent as a string, even boolean-ish/enum fields (e.g.
 * `"true"`, `"sandbox"`) — the API stores config as strings throughout. */
export function updateProviderConfig(
  provider: SubscriptionProvider,
  values: Record<string, string>,
) {
  return apiPut('/subscriptions/admin/config/{provider}', {
    params: { provider },
    body: values,
  });
}

/** Always resolves with HTTP 200 — the result is in `ok`, not the status. */
export function testProviderConnection(provider: SubscriptionProvider) {
  return apiPost('/subscriptions/admin/config/{provider}/test-connection', {
    params: { provider },
  });
}

export function getWebhookUrls() {
  return apiGet('/subscriptions/admin/webhook-urls');
}

export function getProviderProducts(provider: SubscriptionProvider) {
  return apiGet('/subscriptions/admin/products/{provider}', {
    params: { provider },
  });
}

/** `week` isn't in `src/api/schema.ts` yet — the endpoint is being extended
 * to accept it server-side. Cast at the call site below rather than widening
 * the generated type; drop the cast once `generate:api` picks up the change. */
export type ProductPeriodUnit = 'day' | 'week' | 'month' | 'year';

/** Direct write — sets the current price/tier/period now. */
export function putProviderProduct(
  provider: SubscriptionProvider,
  providerProductId: string,
  body: {
    tier: string;
    tier_rank: number;
    period_amount: number;
    period_unit: ProductPeriodUnit;
    price_amount?: number | null;
    price_currency?: string | null;
    mollie_interval?: string | null;
    active?: boolean;
  },
) {
  return apiPut('/subscriptions/admin/products/{provider}/{providerProductId}', {
    params: { provider, providerProductId },
    body: body as typeof body & { period_unit: 'day' | 'month' | 'year' },
  });
}

export function getUserLedger(userId: string) {
  return apiGet('/subscriptions/admin/users/{userId}/ledger', {
    params: { userId },
  });
}

export function grantAccess(
  userId: string,
  body: {
    tier: string;
    period_amount: number;
    period_unit: 'day' | 'month' | 'year';
    reason?: string | null;
  },
) {
  return apiPost('/subscriptions/admin/users/{userId}/grant', {
    params: { userId },
    body,
  });
}

export function recomputeEntitlement(userId: string) {
  return apiPost('/subscriptions/admin/users/{userId}/recompute', {
    params: { userId },
  });
}

export function cancelUserMollieSubscription(userId: string) {
  return apiPost('/subscriptions/admin/users/{userId}/cancel-mollie', {
    params: { userId },
  });
}

export function revokePurchase(purchaseId: string, reason?: string | null) {
  return apiPost('/subscriptions/admin/purchases/{purchaseId}/revoke', {
    params: { purchaseId },
    body: { reason: reason ?? null },
  });
}

export function movePurchase(purchaseId: string, toUserId: string) {
  return apiPost('/subscriptions/admin/purchases/{purchaseId}/move', {
    params: { purchaseId },
    body: { to_user_id: toUserId },
  });
}

/** Despite the name, this never triggers a new charge attempt — it resyncs
 * local status against Mollie. Label the button "status synchroniseren" /
 * "sync status", never "retry". */
export function retryMollieSubscription(mollieSubscriptionId: string) {
  return apiPost('/subscriptions/admin/mollie/{mollieSubscriptionId}/retry', {
    params: { mollieSubscriptionId },
  });
}
